import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, Upload, Link as LinkIcon } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { emptyI18nTriple } from '../../i18n/localizeContent';
import { Button, IconButton, Tabs } from '../../components/UI';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface AvatarFormData {
  name: string;
  whatsappGroupUrl: string;
  photoUrl: string;
  video: { source: 'none' | 'upload' | 'link'; url: string };
  socials: { instagram: string; facebook: string; tiktok: string; youtube: string };
  isActive: boolean;
}

const AvatarForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isEditing = Boolean(id);
  const notifications = useNotifications();
  const { copy } = useCopyToClipboard();
  const photoInputRef = useRef<HTMLInputElement>(null);

  const [i18n, setI18n] = useState<I18nTriple>(emptyI18nTriple());
  const [formData, setFormData] = useState<AvatarFormData>({
    name: '',
    whatsappGroupUrl: '',
    photoUrl: '',
    video: { source: 'none', url: '' },
    socials: { instagram: '', facebook: '', tiktok: '', youtube: '' },
    isActive: true
  });
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const fetchAvatar = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axios.get(`/avatars/${id}`);
      const avatar = response.data;
      setFormData({
        name: avatar.name || '',
        whatsappGroupUrl: avatar.whatsappGroupUrl || '',
        photoUrl: avatar.photoUrl || '',
        video: avatar.video || { source: 'none', url: '' },
        socials: avatar.socials || { instagram: '', facebook: '', tiktok: '', youtube: '' },
        isActive: avatar.isActive !== false
      });
      setSlug(avatar.slug || '');
      setI18n({
        pt: { displayName: avatar.i18n?.pt?.displayName || avatar.displayName || '', headline: avatar.i18n?.pt?.headline || '', bodyHtml: avatar.i18n?.pt?.bodyHtml || '', ctaLabel: avatar.i18n?.pt?.ctaLabel || '' },
        en: { displayName: avatar.i18n?.en?.displayName || '', headline: avatar.i18n?.en?.headline || '', bodyHtml: avatar.i18n?.en?.bodyHtml || '', ctaLabel: avatar.i18n?.en?.ctaLabel || '' },
        es: { displayName: avatar.i18n?.es?.displayName || '', headline: avatar.i18n?.es?.headline || '', bodyHtml: avatar.i18n?.es?.bodyHtml || '', ctaLabel: avatar.i18n?.es?.ctaLabel || '' }
      });
    } catch (error) {
      console.error('Erro ao carregar avatar:', error);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing) fetchAvatar();
  }, [isEditing, fetchAvatar]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingPhoto(true);
      const data = new FormData();
      data.append('photo', file);
      const response = await axios.post('/avatars/upload-photo', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData((prev) => ({ ...prev, photoUrl: response.data.url }));
    } catch (error: any) {
      notifications.showError(t('admin.avatarPhotoUploadError'), error.response?.data?.message || t('admin.avatarTryAgain'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingVideo(true);
      const data = new FormData();
      data.append('video', file);
      const response = await axios.post('/avatars/upload-video', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData((prev) => ({ ...prev, video: { source: 'upload', url: response.data.url } }));
    } catch (error: any) {
      notifications.showError(t('admin.avatarVideoUploadError'), error.response?.data?.message || t('admin.avatarTryAgain'));
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (formData.name.trim().length < 2) {
      notifications.showWarning(t('admin.avatarValidation'), t('admin.avatarNameRequired'));
      setLoading(false);
      return;
    }
    if (!formData.whatsappGroupUrl.trim()) {
      notifications.showWarning(t('admin.avatarValidation'), t('admin.avatarWhatsappRequired'));
      setLoading(false);
      return;
    }
    if (!i18n.pt?.displayName?.trim()) {
      notifications.showWarning(t('admin.avatarValidation'), t('admin.avatarDisplayNameRequired'));
      setLoading(false);
      return;
    }

    const payload = { ...formData, i18n };

    try {
      if (isEditing) {
        await axios.put(`/avatars/${id}`, payload);
        notifications.showSuccess(t('admin.avatarUpdated'), t('admin.avatarUpdatedMsg'));
      } else {
        await axios.post('/avatars', payload);
        notifications.showSuccess(t('admin.avatarCreated'), t('admin.avatarCreatedMsg'));
      }
      setTimeout(() => navigate('/admin/avatars'), 1000);
    } catch (error: any) {
      console.error('Erro ao salvar avatar:', error);
      notifications.showError(t('admin.avatarSaveError'), error.response?.data?.message || t('admin.avatarTryAgain'));
    } finally {
      setLoading(false);
    }
  };

  const publicUrl = slug ? `${window.location.origin}/a/${slug}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center space-x-4">
          <IconButton action="back" onClick={() => navigate('/admin/avatars')} />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{isEditing ? t('admin.avatarEdit') : t('admin.avatarNew')}</h1>
            <p className="text-gray-600">{t('admin.avatarFormDesc')}</p>
          </div>
        </div>

        {publicUrl && (
          <div className="card mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-700 truncate">{publicUrl}</div>
            <Button variant="outline" size="sm" icon="copyLink" onClick={() => copy(publicUrl)}>
              {t('admin.avatarCopyLink')}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.avatarInternalName')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder={t('admin.avatarInternalNamePh') as string}
                    className="field field--lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.avatarWhatsappUrl')}</label>
                  <input
                    type="url"
                    value={formData.whatsappGroupUrl}
                    onChange={(e) => setFormData((prev) => ({ ...prev, whatsappGroupUrl: e.target.value }))}
                    placeholder="https://chat.whatsapp.com/..."
                    className="field field--lg"
                  />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <I18nTripleFields mode="avatar" value={i18n} onChange={setI18n} />
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.avatarPhoto')}</h3>
                {formData.photoUrl && (
                  <img src={formData.photoUrl} alt={t('admin.avatarPhotoAlt') as string} className="h-24 w-24 rounded-full object-cover mb-4" />
                )}
                <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                <Button variant="secondary"
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}>
                  <Upload className="w-4 h-4" />
                  {uploadingPhoto ? t('common.sending') : t('admin.avatarUploadPhoto')}
                </Button>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.avatarVideoOptional')}</h3>
                <Tabs
                  variant="segmented"
                  className="mb-4"
                  value={formData.video.source}
                  onChange={(source) => setFormData((prev) => ({ ...prev, video: { source, url: prev.video.source === source ? prev.video.url : '' } }))}
                  items={[
                    { id: 'upload', label: t('admin.avatarVideoUpload') },
                    { id: 'link', label: t('admin.avatarVideoLink') },
                  ]}
                />
                {formData.video.source === 'upload' ? (
                  <div>
                    {formData.video.url && <p className="text-xs text-gray-500 mb-2 break-all">{formData.video.url}</p>}
                    <input type="file" accept="video/*" onChange={handleVideoUpload} disabled={uploadingVideo} />
                  </div>
                ) : formData.video.source === 'link' ? (
                  <input
                    type="url"
                    value={formData.video.url}
                    onChange={(e) => setFormData((prev) => ({ ...prev, video: { source: 'link', url: e.target.value } }))}
                    placeholder="https://www.youtube.com/embed/..."
                    className="field"
                  />
                ) : (
                  <p className="text-sm text-gray-500 flex items-center gap-1.5"><LinkIcon className="w-4 h-4" /> {t('admin.avatarNoVideo')}</p>
                )}
              </div>

              <div className="form-actions">
                <Button variant="secondary" type="button" onClick={() => navigate('/admin/avatars')}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={loading}>
                  <Save className="w-4 h-4" />
                  <span>{loading ? t('common.saving') : t('common.save')}</span>
                </Button>
              </div>
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.avatarColStatus')}</h3>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-medium text-gray-700">{t('admin.active')}</span>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                  className="check-input"
                />
              </label>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.avatarSocialsOptional')}</h3>
              <div className="space-y-3">
                {(['instagram', 'facebook', 'tiktok', 'youtube'] as const).map((key) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 mb-1 capitalize">{key}</label>
                    <input
                      type="text"
                      value={formData.socials[key]}
                      onChange={(e) => setFormData((prev) => ({ ...prev, socials: { ...prev.socials, [key]: e.target.value } }))}
                      className="field"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarForm;
